import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    X,
    Plus,
    Info,
    Loader2
} from 'lucide-react';

import { useToast } from './toastContext';
import { useSensorDictionaries } from './useSensorDictionaries';

import {
    API_URL,
    DEVICE_TYPE_NAMES,
    DEVICE_TYPE_MAP,
    DEVICE_PLACEHOLDERS,
    POSITION_OPTIONS,
    HC_MIT_NUMBERS,
    formatDateForInput,
    formatHex
} from './houseHelpers';

const CreateDeviceModal = ({
    type,
    city,
    street,
    house,
    onClose,
    onSave
}) => {
    const houseNumber = house.replace('Дом ', '').trim();

    const {
        temperatureSensors,
        pressureSensors,
        flowMeters
    } = useSensorDictionaries();

    const toast = useToast();

    const [deviceType, setDeviceType] = useState('');
    const [deviceModel, setDeviceModel] = useState('');
    const [deviceName, setDeviceName] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [factoryNumber, setFactoryNumber] = useState('');
    const [position, setPosition] = useState('');
    const [verificationDate, setVerificationDate] = useState('');
    const [validUntil, setValidUntil] = useState('');

    const [sensorDeviceType, setSensorDeviceType] = useState('');
    const [sensorDeviceModel, setSensorDeviceModel] = useState('');
    const [sensorSpecs, setSensorSpecs] = useState('');
    const [sensorReleaseDate, setSensorReleaseDate] = useState('');
    const [selectedSensorDict, setSelectedSensorDict] = useState(null);

    const [releaseDate, setReleaseDate] = useState('');
    const [crc16, setCrc16] = useState('');
    const [lastSettingsChange, setLastSettingsChange] = useState('');
    const [jumperPosition, setJumperPosition] = useState('Изменения запрещены');
    const [algorithmFormula, setAlgorithmFormula] = useState('');
    const [coldSourceParam, setColdSourceParam] = useState('6,5 °C');
    const [archiveDepth, setArchiveDepth] = useState('4 года');

    const [availableDevices, setAvailableDevices] = useState([]);
    const [selectedDeviceIndex, setSelectedDeviceIndex] = useState(-1);
    const [isLoadingDevices, setIsLoadingDevices] = useState(false);
    const [loadingFgis, setLoadingFgis] = useState(false);

    const isHeatComputer = type === 'heat-computer';
    const isSensor = type !== 'heat-computer';

    const placeholders = DEVICE_PLACEHOLDERS[type] || {};

    const getSensorDictionaries = () => {
        if (type === 'temp-sensor') return temperatureSensors;
        if (type === 'pressure-sensor') return pressureSensors;
        if (type === 'flow-meter') return flowMeters;

        return [];
    };

    const fetchFgisData = async (serialNumber, mitNumber) => {
        if (!serialNumber || !mitNumber) return;

        setLoadingFgis(true);

        try {
            const token = localStorage.getItem('token');

            const response = await axios.get(
                `${API_URL}/api/fgis-arshin/verify?serialNumber=${encodeURIComponent(serialNumber)}&mitNumber=${encodeURIComponent(mitNumber)}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (response.data && response.data.success !== false) {
                if (response.data.verificationDate) {
                    setVerificationDate(formatDateForInput(response.data.verificationDate));
                }

                if (response.data.validDate) {
                    setValidUntil(formatDateForInput(response.data.validDate));
                }

                if (response.data.manufactureYear) {
                    const year = response.data.manufactureYear;
                    const dateStr = `${year}-01-01`;

                    if (isHeatComputer) {
                        setReleaseDate(dateStr);
                    } else {
                        setSensorReleaseDate(dateStr);
                    }
                }
            }
        } catch (err) {
            console.error('Ошибка загрузки данных из ФГИС:', err);
        } finally {
            setLoadingFgis(false);
        }
    };

    const loadAvailableDevices = async () => {
        setIsLoadingDevices(true);

        try {
            const token = localStorage.getItem('token');

            const response = await axios.get(
                `${API_URL}/api/houses/${encodeURIComponent(city)}/${encodeURIComponent(street)}/${encodeURIComponent(houseNumber)}/devices`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setAvailableDevices(response.data || []);
        } catch (err) {
            console.error('Ошибка загрузки списка устройств:', err);
        } finally {
            setIsLoadingDevices(false);
        }
    };

    const loadDeviceSettings = async (selectedDevice) => {
        try {
            const token = localStorage.getItem('token');

            let endpoint;

            switch (selectedDevice.deviceType) {
                case 'Aksioma01':
                    endpoint = `/api/devices/aksioma01/${selectedDevice.deviceName}/info`;
                    break;

                case 'Aksioma03':
                    endpoint = `/api/devices/aksioma03/${selectedDevice.deviceName}/info`;
                    break;

                case 'Karat307':
                    endpoint = `/api/devices/karat307/${selectedDevice.deviceName}/info`;
                    break;

                default:
                    return;
            }

            const response = await axios.get(`${API_URL}${endpoint}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            autofillFromSettings(response.data, selectedDevice.deviceType);
        } catch (err) {
            console.error('Ошибка загрузки настроек устройства:', err);
            toast.warning('Не удалось загрузить настройки устройства');
        }
    };

    const autofillFromSettings = (settings, deviceType) => {
        const formatDate = (dateStr) => {
            if (!dateStr) return '';

            const datePart = dateStr.split(/[ T]/)[0];

            if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
                return datePart;
            }

            if (/^\d{2}\.\d{2}\.\d{4}$/.test(datePart)) {
                const [day, month, year] = datePart.split('.');

                return `${year}-${month}-${day}`;
            }

            return '';
        };

        if (deviceType === 'Aksioma01') {
            setFactoryNumber(settings.serial_number || '');
            setCrc16(settings.settings_crc || '');
            setLastSettingsChange(formatDate(settings.lastModDatetime));

            setJumperPosition(
                settings.switch_save === 'Разрешено'
                    ? 'Изменения разрешены'
                    : 'Изменения запрещены'
            );

            if (settings.sis_count_si && settings.sis_count_ft) {
                setAlgorithmFormula(
                    `СИ №${settings.sis_count_si}, ФТ №${settings.sis_count_ft}`
                );
            }
        } else if (deviceType === 'Aksioma03') {
            setFactoryNumber(settings.serial_number || '');
            setCrc16(settings.settingsCRC16 ? formatHex(settings.settingsCRC16) : '');
            setLastSettingsChange(formatDate(settings.lastModDateTime));

            setJumperPosition(
                settings.switchSaveButton === 1
                    ? 'Изменения разрешены'
                    : 'Изменения запрещены'
            );
        } else if (deviceType === 'Karat307') {
            setFactoryNumber(settings.serialNumber || '');
            setCrc16(settings.configChecksum || '');
            setLastSettingsChange(formatDate(settings.lastEditTime));
            setJumperPosition('Изменения запрещены');
        }
    };

    const handleDeviceSelect = (e) => {
        const index = parseInt(e.target.value, 10);

        setSelectedDeviceIndex(index);

        const selectedDevice = availableDevices[index];

        if (selectedDevice) {
            setDeviceType(selectedDevice.deviceType);

            setDeviceModel(
                selectedDevice.deviceModel || DEVICE_TYPE_MAP[selectedDevice.deviceType] || ''
            );

            setDeviceName(selectedDevice.deviceName);
            setDisplayName(selectedDevice.displayName || '');

            setVerificationDate('');
            setValidUntil('');
            setReleaseDate('');

            loadDeviceSettings(selectedDevice);
        }
    };

    const handleSensorTypeChange = (e) => {
        const dictId = e.target.value;

        if (!dictId) {
            setSelectedSensorDict(null);
            setSensorDeviceType('');
            return;
        }

        const dicts = getSensorDictionaries();

        const selected = dicts.find((d) => d.id === parseInt(dictId, 10));

        if (selected) {
            setSelectedSensorDict(selected);
            setSensorDeviceType(selected.designation || selected.name);
        }
    };

    const handleSave = () => {
        if (isHeatComputer) {
            if (selectedDeviceIndex === -1 || !deviceType) {
                toast.warning('Выберите устройство из списка');
                return;
            }

            onSave({
                deviceType,
                deviceModel: deviceModel || null,
                deviceName: deviceName || null,
                displayName: displayName || null,
                factoryNumber: factoryNumber || null,
                verificationDate: verificationDate || null,
                verificationValidUntil: validUntil || null,
                releaseDate: releaseDate || null,
                crc16: crc16 || null,
                lastSettingsChange: lastSettingsChange || null,
                jumperPosition: jumperPosition || null,
                algorithmFormula: algorithmFormula || null,
                coldSourceParam: coldSourceParam || null,
                archiveDepth: archiveDepth || null
            });
        } else {
            onSave({
                displayName: null,
                factoryNumber: factoryNumber || null,
                verificationDate: verificationDate || null,
                verificationValidUntil: validUntil || null,
                position: position || null,
                deviceType: sensorDeviceType || null,
                deviceModel: sensorDeviceModel || null,
                specs: sensorSpecs || null,
                releaseDate: sensorReleaseDate || null,
                registryNumber: selectedSensorDict?.registryNumber || null
            });
        }
    };

    useEffect(() => {
        if (isHeatComputer && city && street && houseNumber) {
            loadAvailableDevices();
        }
    }, [isHeatComputer, city, street, houseNumber]);

    useEffect(() => {
        if (availableDevices.length > 0 && selectedDeviceIndex === -1) {
            setSelectedDeviceIndex(0);

            const selectedDevice = availableDevices[0];

            setDeviceType(selectedDevice.deviceType);

            setDeviceModel(
                selectedDevice.deviceModel || DEVICE_TYPE_MAP[selectedDevice.deviceType] || ''
            );

            setDeviceName(selectedDevice.deviceName);
            setDisplayName(selectedDevice.displayName || '');
            setFactoryNumber(selectedDevice.deviceName || '');

            loadDeviceSettings(selectedDevice);
        }
    }, [availableDevices]);

    useEffect(() => {
        if (isHeatComputer && factoryNumber && deviceType) {
            const mitNumber = HC_MIT_NUMBERS[deviceType];

            if (mitNumber) {
                fetchFgisData(factoryNumber, mitNumber);
            }
        }
    }, [factoryNumber, deviceType]);

    useEffect(() => {
        if (isSensor && factoryNumber && selectedSensorDict?.registryNumber) {
            fetchFgisData(factoryNumber, selectedSensorDict.registryNumber);
        }
    }, [factoryNumber, selectedSensorDict]);

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800 z-10">
                    <h3 className="text-white font-semibold">
                        Добавить: {DEVICE_TYPE_NAMES[type]}
                    </h3>

                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-white"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    {isHeatComputer && (
                        <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/30">
                            <h4 className="text-blue-300 font-medium text-sm mb-3 flex items-center gap-2">
                                <Info className="w-4 h-4" />
                                Выберите существующее устройство
                            </h4>

                            {isLoadingDevices ? (
                                <div className="flex items-center gap-2 text-slate-400 text-sm">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Загрузка списка устройств...
                                </div>
                            ) : availableDevices.length === 0 ? (
                                <div className="text-slate-400 text-sm bg-slate-900/50 p-3 rounded border border-slate-700">
                                    По этому адресу нет доступных устройств для выбора.
                                </div>
                            ) : (
                                <select
                                    value={selectedDeviceIndex}
                                    onChange={handleDeviceSelect}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {availableDevices.map((d, idx) => (
                                        <option key={idx} value={idx}>
                                            {DEVICE_TYPE_MAP[d.deviceType] || d.deviceType} —{' '}
                                            {d.deviceName || 'Без названия'}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}

                    {isHeatComputer && selectedDeviceIndex !== -1 && (
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                            <h4 className="text-white font-medium text-sm mb-3">
                                Информация об устройстве
                            </h4>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Наименование в системе
                                    </label>

                                    <input
                                        type="text"
                                        value={deviceName}
                                        readOnly
                                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 text-sm cursor-not-allowed"
                                    />
                                </div>

                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Исполнение (Модель)
                                    </label>

                                    <input
                                        type="text"
                                        value={deviceModel}
                                        readOnly
                                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 text-sm cursor-not-allowed"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {isSensor && (
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                            <h4 className="text-white font-medium text-sm mb-3">
                                Характеристики устройства
                            </h4>

                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Тип СИ *
                                    </label>

                                    <select
                                        value={selectedSensorDict?.id || ''}
                                        onChange={handleSensorTypeChange}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                    >
                                        <option value="">— Выберите тип СИ —</option>

                                        {getSensorDictionaries().map((dict) => (
                                            <option key={dict.id} value={dict.id}>
                                                {dict.designation || dict.name} (рег. № {dict.registryNumber})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Исполнение (Модель)
                                    </label>

                                    <input
                                        type="text"
                                        value={sensorDeviceModel}
                                        onChange={(e) => setSensorDeviceModel(e.target.value)}
                                        placeholder={placeholders.model || 'Модель устройства'}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-slate-400 text-xs mb-1">
                                    Технические характеристики
                                </label>

                                <textarea
                                    value={sensorSpecs}
                                    onChange={(e) => setSensorSpecs(e.target.value)}
                                    placeholder={placeholders.specs || 'Технические характеристики'}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm min-h-[100px] resize-y whitespace-pre-wrap"
                                />
                            </div>
                        </div>
                    )}

                    <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                        <h4 className="text-white font-medium text-sm mb-3">
                            Основная информация
                        </h4>

                        <div className="grid grid-cols-2 gap-3">
                            {!isSensor && (
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Описание
                                    </label>

                                    <input
                                        type="text"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-slate-400 text-xs mb-1">
                                    Заводской номер
                                </label>

                                <input
                                    type="text"
                                    value={factoryNumber}
                                    onChange={(e) => setFactoryNumber(e.target.value)}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-slate-400 text-xs mb-1">
                                    Дата поверки
                                </label>

                                <input
                                    type="date"
                                    value={verificationDate}
                                    onChange={(e) => setVerificationDate(e.target.value)}
                                    disabled={loadingFgis}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer disabled:opacity-60"
                                />
                            </div>

                            <div>
                                <label className="block text-slate-400 text-xs mb-1">
                                    Поверка действительна до
                                </label>

                                <input
                                    type="date"
                                    value={validUntil}
                                    onChange={(e) => setValidUntil(e.target.value)}
                                    disabled={loadingFgis}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer disabled:opacity-60"
                                />
                            </div>

                            <div className="col-span-2">
                                <label className="block text-slate-400 text-xs mb-1">
                                    Дата выпуска
                                </label>

                                <input
                                    type="date"
                                    value={isHeatComputer ? releaseDate : sensorReleaseDate}
                                    onChange={(e) =>
                                        isHeatComputer
                                            ? setReleaseDate(e.target.value)
                                            : setSensorReleaseDate(e.target.value)
                                    }
                                    disabled={loadingFgis}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer disabled:opacity-60"
                                />
                            </div>

                            {isSensor && (
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Переменная
                                    </label>

                                    <select
                                        value={position}
                                        onChange={(e) => setPosition(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                    >
                                        <option value="">— Выберите —</option>

                                        {POSITION_OPTIONS[type]?.map((pos) => (
                                            <option key={pos} value={pos}>
                                                {pos}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {loadingFgis && (
                            <div className="mt-3 flex items-center gap-2 text-blue-400 text-xs">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Загрузка данных из ФГИС Аршин...
                            </div>
                        )}
                    </div>

                    {isHeatComputer && (
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                            <h4 className="text-white font-medium text-sm mb-3">
                                Параметры программирования
                            </h4>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        CRC16 настроек
                                    </label>

                                    <input
                                        type="text"
                                        value={crc16}
                                        onChange={(e) => setCrc16(e.target.value)}
                                        placeholder="0x2DAC"
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                    />
                                </div>

                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Дата посл. изм. настроек
                                    </label>

                                    <input
                                        type="date"
                                        value={lastSettingsChange}
                                        onChange={(e) => setLastSettingsChange(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                    />
                                </div>

                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Перемычка
                                    </label>

                                    <select
                                        value={jumperPosition}
                                        onChange={(e) => setJumperPosition(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                    >
                                        <option value="Изменения запрещены">
                                            Изменения запрещены
                                        </option>

                                        <option value="Изменения разрешены">
                                            Изменения разрешены
                                        </option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Глубина архивов
                                    </label>

                                    <input
                                        type="text"
                                        value={archiveDepth}
                                        onChange={(e) => setArchiveDepth(e.target.value)}
                                        placeholder="4 года"
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                    />
                                </div>
                            </div>

                            <div className="space-y-3 mt-3">
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Алгоритм учета
                                    </label>

                                    <input
                                        type="text"
                                        value={algorithmFormula}
                                        onChange={(e) => setAlgorithmFormula(e.target.value)}
                                        placeholder="M1(h1-hx) - M2(h2-hx)"
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                    />
                                </div>

                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Параметр холодного источника
                                    </label>

                                    <input
                                        type="text"
                                        value={coldSourceParam}
                                        onChange={(e) => setColdSourceParam(e.target.value)}
                                        placeholder="6,5 °C"
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-4 py-3 border-t border-slate-700 flex justify-end gap-2 sticky bottom-0 bg-slate-800">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
                    >
                        Отмена
                    </button>

                    <button
                        onClick={handleSave}
                        disabled={isHeatComputer && selectedDeviceIndex === -1}
                        className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Plus className="w-4 h-4" />
                        Создать
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateDeviceModal;